import React, { useState, useEffect } from 'react';
import { driverAPI } from '../../lib/api';
import { formatCurrency, formatDate } from '../../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { DollarSign, TrendingUp, Calendar, Gift, Loader2 } from 'lucide-react';

const DriverEarnings = () => {
  const [loading, setLoading] = useState(true);
  const [earnings, setEarnings] = useState(null);

  useEffect(() => {
    loadEarnings();
  }, []);

  const loadEarnings = async () => {
    try {
      const response = await driverAPI.getEarnings();
      setEarnings(response.data);
    } catch (error) {
      console.error('Error loading earnings:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-red-600" />
      </div>
    );
  }

  // Calculate tips from recent rides
  const totalTips = earnings?.recent_rides?.reduce((sum, ride) => sum + (ride.tip_amount || 0), 0) || 0;

  return (
    <div className="p-6" data-testid="driver-earnings-page">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-3xl font-display font-bold text-slate-900 dark:text-white uppercase tracking-tight mb-8">
          Your Earnings
        </h1>

        {/* Summary Cards */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card className="bg-gradient-to-br from-green-500 to-green-600 border-0">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-green-100">Total Earnings</p>
                  <p className="text-3xl font-display font-bold text-white mt-1">
                    {formatCurrency(earnings?.total_earnings || 0)}
                  </p>
                </div>
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                  <DollarSign className="w-6 h-6 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500 dark:text-slate-400">This Week</p>
                  <p className="text-3xl font-display font-bold text-slate-900 dark:text-white mt-1">
                    {formatCurrency(earnings?.weekly_earnings || 0)}
                  </p>
                </div>
                <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-xl flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500 dark:text-slate-400">Total Rides</p>
                  <p className="text-3xl font-display font-bold text-slate-900 dark:text-white mt-1">
                    {earnings?.total_rides || 0}
                  </p>
                </div>
                <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/30 rounded-xl flex items-center justify-center">
                  <Calendar className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500 dark:text-slate-400">Tips Received</p>
                  <p className="text-3xl font-display font-bold text-slate-900 dark:text-white mt-1">
                    {formatCurrency(totalTips)}
                  </p>
                </div>
                <div className="w-12 h-12 bg-amber-100 dark:bg-amber-900/30 rounded-xl flex items-center justify-center">
                  <Gift className="w-6 h-6 text-amber-600 dark:text-amber-400" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Earnings Breakdown */}
        <Card className="mb-8 bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
          <CardHeader>
            <CardTitle className="font-display uppercase tracking-tight">How You Earn</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid sm:grid-cols-3 gap-6">
              <div className="p-4 bg-slate-50 dark:bg-slate-700/50 rounded-xl text-center">
                <p className="text-4xl font-display font-bold text-red-600">85%</p>
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Of every fare goes to you</p>
              </div>
              <div className="p-4 bg-slate-50 dark:bg-slate-700/50 rounded-xl text-center">
                <p className="text-4xl font-display font-bold text-green-600">100%</p>
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Of tips are yours</p>
              </div>
              <div className="p-4 bg-slate-50 dark:bg-slate-700/50 rounded-xl text-center">
                <p className="text-4xl font-display font-bold text-blue-600">$0.90-$2</p>
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Per mile rate</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Recent Earnings */}
        <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
          <CardHeader>
            <CardTitle className="font-display uppercase tracking-tight">Recent Earnings</CardTitle>
          </CardHeader>
          <CardContent>
            {earnings?.recent_rides?.length > 0 ? (
              <div className="space-y-3">
                {earnings.recent_rides.map((ride, index) => (
                  <div 
                    key={index} 
                    className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-700/50 rounded-xl"
                  >
                    <div>
                      <p className="font-medium text-slate-900 dark:text-white">
                        {ride.distance_miles?.toFixed(1)} miles · {ride.duration_minutes} min
                      </p>
                      <p className="text-sm text-slate-500 dark:text-slate-400">
                        {formatDate(ride.completed_at)}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-display font-bold text-lg text-green-600">
                        +{formatCurrency(ride.driver_payout)}
                      </p>
                      {ride.tip_amount > 0 && (
                        <p className="text-sm text-amber-600 dark:text-amber-400">
                          +{formatCurrency(ride.tip_amount)} tip
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-slate-500 dark:text-slate-400 py-8">
                Complete rides to see your earnings here
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DriverEarnings;
